import * as React from "react";
import { VariableSizeGrid } from "react-window";

function getTilesPerRowAndRemainder(
  minMargin: number,
  containerWidth: number,
  min: number
) {
  let nrOfTilesPerRow = 0;
  let accumulation = 0;

  while (true) {
    accumulation = accumulation + min;
    nrOfTilesPerRow++;

    if (
      accumulation + minMargin >= containerWidth ||
      accumulation + min >= containerWidth
    ) {
      break;
    }

    accumulation = accumulation + minMargin;
  }

  return {
    nrOfTilesPerRow,
    remainder: containerWidth - accumulation
  };
}

function getColumnData(
  minMargin: number,
  containerWidth: number,
  min: number,
  max: number
) {
  const { nrOfTilesPerRow, remainder } = getTilesPerRowAndRemainder(
    minMargin,
    containerWidth,
    min
  );

  const remainingPerTile = remainder / nrOfTilesPerRow;
  const headroomPerTile = max - min;

  if (remainingPerTile <= headroomPerTile) {
    return {
      nrOfTilesPerRow,
      tileWidth: Math.floor(min + remainingPerTile),
      margin: minMargin
    };
  }

  return {
    nrOfTilesPerRow,
    tileWidth: Math.floor(max),
    margin: Math.floor(
      minMargin +
        (remainder - nrOfTilesPerRow * headroomPerTile) / (nrOfTilesPerRow - 1)
    )
  };
}

const createSizesArray = (
  amount: number,
  creator: (index: number) => number
) => {
  return Array(amount)
    .fill(true)
    .map((_, index) => creator(index));
};

type State = {
  dimensions: {
    width: number;
    height: number;
  } | null;
  totalColumns: number | null;
  totalRows: number | null;
  cellWidths: number[] | null;
  cellHeights: number[] | null;
  tileWidth: number | null;
};

type Args = {
  minMargin: number;
  minTileWidth: number;
  maxTileWidth: number;
  tileHeight: number;
  amountOfChildren: number;
};

function useSizing({
  minMargin,
  minTileWidth,
  maxTileWidth,
  tileHeight,
  amountOfChildren
}: Args) {
  const resizeRef = React.useRef<HTMLDivElement>(null);
  const gridRef = React.useRef<VariableSizeGrid>(null);
  const [state, setState] = React.useState<State>({
    dimensions: null,
    cellHeights: null,
    cellWidths: null,
    tileWidth: null,
    totalColumns: null,
    totalRows: null
  });

  const animationFrameRef = React.useRef<any>(null);

  React.useLayoutEffect(() => {
    const handleResize = () => {
      const el = resizeRef.current!;

      const width = el.offsetWidth;
      const height = el.offsetHeight;

      const { margin, nrOfTilesPerRow, tileWidth } = getColumnData(
        minMargin,
        width,
        minTileWidth,
        maxTileWidth
      );

      const totalRows = Math.ceil(amountOfChildren / nrOfTilesPerRow);

      setState({
        dimensions: {
          width,
          height
        },
        tileWidth,
        cellHeights: createSizesArray(totalRows, index => {
          if (index === totalRows - 1) {
            return tileHeight;
          }

          return tileHeight + margin;
        }),
        cellWidths: createSizesArray(nrOfTilesPerRow, index => {
          if (index === nrOfTilesPerRow - 1) {
            return tileWidth;
          }

          return tileWidth + margin;
        }),
        totalColumns: nrOfTilesPerRow,
        totalRows
      });
    };

    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [amountOfChildren]);

  // force react-window to recalculate column-widths
  React.useLayoutEffect(() => {
    if (gridRef.current) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }

      animationFrameRef.current = requestAnimationFrame(() => {
        gridRef.current!.resetAfterColumnIndex(0);
        gridRef.current!.resetAfterRowIndex(0);
      });
    }
  }, [state.dimensions]);

  return {
    resizeRef,
    gridRef,
    ...state
  };
}

export default useSizing;
